import moment from "moment";
import {
    MANUFACTURE_ELABORATION,
    PRODUCT_ELABORATION,
    SUPPLIER_ELABORATION,
} from "../../../constants/import";

const EVENT_TYPE_CONFIRMED_QTY = "CONFIRMED_QTY";

const toNumericOrNull = (value) => {
    if (value === null || value === undefined || value === "") {
        return null;
    }
    const numeric = Number(value);
    return Number.isFinite(numeric) ? numeric : null;
};

const extractEventsArray = (value) => {
    if (!value) {
        return [];
    }
    if (Array.isArray(value)) {
        return value;
    }
    if (typeof value === "object") {
        const candidates = ["events", "data", "items", "rows"];
        for (const key of candidates) {
            if (Array.isArray(value[key])) {
                return value[key];
            }
        }
    }
    return [];
};

const resolveAssetReference = (candidate) => {
    if (!candidate) {
        return null;
    }

    if (typeof candidate === "string") {
        const trimmed = candidate.trim();
        return trimmed ? trimmed : null;
    }

    if (Array.isArray(candidate)) {
        for (const entry of candidate) {
            const resolved = resolveAssetReference(entry);
            if (resolved) {
                return resolved;
            }
        }
        return null;
    }

    if (typeof candidate === "object") {
        const keys = ["reference", "url", "href", "path"];
        for (const key of keys) {
            const value = candidate[key];
            if (typeof value === "string") {
                const trimmed = value.trim();
                if (trimmed) {
                    return trimmed;
                }
            }
        }
    }

    return null;
};

const collectConfirmedQuantityEvents = (item) => {
    if (!item || typeof item !== "object") {
        return [];
    }

    const sources = [
        item.confirmed_quantity_events,
        item.confirmedQuantityEvents,
        item.document_events,
        item.documentEvents,
        item.events,
        item.body_events,
        item.bodyEvents,
    ];

    const seenIds = new Set();
    const result = [];

    sources.forEach((source) => {
        const list = extractEventsArray(source);
        list.forEach((entry) => {
            if (!entry || typeof entry !== "object") {
                return;
            }
            const rawType = entry.type ?? entry.event_type ?? entry.eventType ?? "";
            const normalizedType = typeof rawType === "string" ? rawType.toUpperCase() : "";
            if (normalizedType && normalizedType !== EVENT_TYPE_CONFIRMED_QTY) {
                return;
            }
            const quantityCandidate = entry.quantity ?? entry.confirmed_quantity ?? entry.qty ?? entry.value;
            if (!normalizedType && toNumericOrNull(quantityCandidate) === null) {
                return;
            }
            const eventId = entry.id ?? entry.event_id ?? entry.eventId ?? entry.uuid ?? null;
            if (eventId) {
                if (seenIds.has(eventId)) {
                    return;
                }
                seenIds.add(eventId);
            }
            result.push(entry);
        });
    });

    return result;
};

const sumEventQuantities = (events) => {
    if (!Array.isArray(events) || events.length === 0) {
        return 0;
    }
    return events.reduce((accumulator, entry) => {
        const quantityValue = toNumericOrNull(
            entry?.quantity
            ?? entry?.confirmed_quantity
            ?? entry?.qty
            ?? entry?.value
        );
        return accumulator + (quantityValue ?? 0);
    }, 0);
};

const resolveRowHiddenFlag = (item) => {
    if (!item || typeof item !== "object") {
        return false;
    }

    const directCandidates = [
        item.hide,
        item.hidden,
        item.is_hidden,
        item.isHidden,
    ];
    const directMatch = directCandidates.find((candidate) => candidate !== undefined && candidate !== null);
    if (directMatch !== undefined) {
        return Boolean(directMatch);
    }

    const visibilityState =
        item.visibility
        ?? item.visibility_status
        ?? item.visibilityStatus
        ?? null;
    if (typeof visibilityState === "string") {
        return visibilityState.toLowerCase() === "hidden";
    }

    const visibilityFlags = [
        item.is_visible,
        item.isVisible,
        item.visible,
    ];
    const flagMatch = visibilityFlags.find((candidate) => candidate !== undefined && candidate !== null);
    if (flagMatch !== undefined) {
        return !Boolean(flagMatch);
    }

    const hiddenTimestamp = item.hidden_at ?? item.hiddenAt ?? null;
    if (hiddenTimestamp !== null && hiddenTimestamp !== undefined && hiddenTimestamp !== "") {
        return true;
    }

    return false;
};

export const NUMERIC_FIELDS = new Set([
    "quantity",
    "list_price",
    "unit_price",
    "purchase_price",
    "net_price",
    "volume",
    "gross_weight",
    "net_weight",
    "qty_per_packaging",
    "length",
    "width",
    "height",
    "radius",
    "total",
    "total_net_price",
]);

export const DOCUMENT_BODY_IMPORT_TYPE = "document_body";

const mergeImportFields = (...groups) => Array.from(new Set(
    groups
        .filter((group) => Array.isArray(group))
        .flat()
));

const DOCUMENT_BODY_IMPORT_FIELDS = [
    "name",
    "product_reference",
    "quantity",
    "list_price",
    "list_price_discounts",
    "unit_price",
    "unit_price_discounts",
    "purchase_price",
    "net_price",
    "total_net_price",
    "delivery_date",
    "volume",
    "gross_weight",
    "net_weight",
    "qty_per_packaging",
    "length",
    "width",
    "height",
    "radius",
    "note"
];

export const DOCUMENT_BODY_IMPORT_CONFIG = {
    type: DOCUMENT_BODY_IMPORT_TYPE,
    fields: DOCUMENT_BODY_IMPORT_FIELDS,
    imports: [
        {
            value: DOCUMENT_BODY_IMPORT_TYPE,
            label: "Import document rows",
            type: DOCUMENT_BODY_IMPORT_TYPE,
            params: [
                {
                    type: PRODUCT_ELABORATION.type,
                    label: "Import through Product Code",
                    description: "Use the product code for a fast and efficient import. This option facilitates the synchronization of existing data and the creation of new products on our platform.",
                    data: mergeImportFields(PRODUCT_ELABORATION.fields, DOCUMENT_BODY_IMPORT_FIELDS),
                },
                {
                    type: MANUFACTURE_ELABORATION.type,
                    label: "Import data using the manufacturer's code",
                    description: "Import data using the manufacturer's code. However, to make the most of this option, it is necessary that the related product is already present on the platform, otherwise the import will not be successful.",
                    data: mergeImportFields(MANUFACTURE_ELABORATION.fields, DOCUMENT_BODY_IMPORT_FIELDS),
                },
                {
                    type: SUPPLIER_ELABORATION.type,
                    label: "Import through Supplier Code",
                    description: "The supplier code option offers an additional import method. However, it is important to note that, to take advantage of this option, the related product must already be present on the platform, otherwise the import will not be successful.",
                    data: mergeImportFields(SUPPLIER_ELABORATION.fields, DOCUMENT_BODY_IMPORT_FIELDS),
                },
            ],
        },
    ],
};

export const getCurrencySymbolFromCode = (code) => {
    if (!code || typeof code !== "string") {
        return null;
    }

    try {
        const formatter = new Intl.NumberFormat(undefined, {
            style: "currency",
            currency: code,
            currencyDisplay: "narrowSymbol",
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
        });
        if (typeof formatter.formatToParts === "function") {
            const parts = formatter.formatToParts(0);
            if (Array.isArray(parts)) {
                const symbolPart = parts.find((part) => part.type === "currency");
                if (symbolPart?.value) {
                    return symbolPart.value;
                }
            }
        }
        const formatted = formatter.format(0);
        const extracted = formatted.replace(/[0-9.,\s]/g, "").trim();
        return extracted || null;
    } catch (error) {
        return null;
    }
};

export const resolveDocumentCurrencySymbol = (document, fallbackSymbol = "") => {
    const directSymbol = document?.currency?.currency_symbol
        ?? document?.company?.currency?.currency_symbol
        ?? document?.currency_symbol
        ?? fallbackSymbol
        ?? "";
    if (directSymbol) {
        return directSymbol;
    }

    const currencyCode = document?.currency?.code
        ?? document?.currency_code
        ?? document?.company?.currency?.code
        ?? null;
    const derivedSymbol = getCurrencySymbolFromCode(currencyCode);
    return derivedSymbol ?? fallbackSymbol ?? "";
};

export const resolveDocumentType = (document, explicitType = null) => {
    if (explicitType) {
        return explicitType;
    }

    if (document?.document_type?.code) {
        return document.document_type.code;
    }

    if (typeof document?.document_type === "string") {
        return document.document_type;
    }

    if (document?.header?.document_type?.code) {
        return document.header.document_type.code;
    }

    if (typeof document?.header?.document_type === "string") {
        return document.header.document_type;
    }

    if (document?.header?.type) {
        return document.header.type;
    }

    if (document?.type) {
        return document.type;
    }

    if (document?.document_type_code) {
        return document.document_type_code;
    }

    if (document?.type_code) {
        return document.type_code;
    }

    return null;
};

export const normalizeDiscountValue = (value) => {
    if (value === null || value === undefined || value === "") {
        return null;
    }
    if (typeof value === "number") {
        return Number.isFinite(value) ? value : null;
    }
    if (typeof value === "string") {
        const cleaned = value.replace(/[%\s]+/g, "").replace(",", ".");
        if (cleaned === "") {
            return null;
        }
        const numeric = Number(cleaned);
        return Number.isNaN(numeric) ? null : numeric;
    }
    return null;
};

export const normalizeDiscountList = (input) => {
    if (input === null || input === undefined) {
        return [];
    }
    const source = Array.isArray(input)
        ? input
        : typeof input === "string"
            ? input.split("|")
            : [input];
    const candidates = [];
    const collect = (entry) => {
        if (entry === null || entry === undefined) {
            return;
        }
        if (Array.isArray(entry)) {
            entry.forEach(collect);
            return;
        }
        if (typeof entry === "object") {
            if (Array.isArray(entry.values)) {
                entry.values.forEach(collect);
                return;
            }
            if (Array.isArray(entry.entries)) {
                entry.entries.forEach(collect);
                return;
            }
            const objectCandidates = [
                entry.value,
                entry.discount,
                entry.percentage,
                entry.amount,
                entry.rate,
            ];
            const match = objectCandidates.find((candidate) => candidate !== null && candidate !== undefined);
            if (match !== undefined) {
                collect(match);
            }
            return;
        }
        candidates.push(entry);
    };
    source.forEach(collect);
    return candidates
        .map(normalizeDiscountValue)
        .filter((value) => value !== null);
};

export const stringifyDiscountList = (entries) => (
    Array.isArray(entries)
        ? entries
            .map((value) => {
                if (typeof value === "number" && Number.isFinite(value)) {
                    return value;
                }
                if (typeof value === "string") {
                    const cleaned = value.replace(/[%\s]+/g, "").replace(",", ".");
                    const numeric = Number(cleaned);
                    return Number.isNaN(numeric) ? null : numeric;
                }
                return null;
            })
            .filter((value) => value !== null)
        : []
);

export const applyDiscountsToValue = (value, discounts) => {
    const normalizedValue = Number(value) || 0;
    const normalizedDiscounts = normalizeDiscountList(discounts);
    return normalizedDiscounts.reduce((accumulator, entry) => {
        const numericDiscount = Number(entry);
        if (!Number.isFinite(numericDiscount)) {
            return accumulator;
        }
        const factor = 1 - (numericDiscount / 100);
        return Number((accumulator * factor).toFixed(6));
    }, normalizedValue);
};

export const calculateTotalFromNetPrice = (quantity, netPrice) => {
    const normalizedQuantity = Number(quantity) || 0;
    const normalizedNetPrice = Number(netPrice) || 0;
    return Number((normalizedQuantity * normalizedNetPrice).toFixed(2));
};

export const deriveNetPrice = (unitPrice, discounts, explicitNetPrice) => {
    if (explicitNetPrice !== null && explicitNetPrice !== undefined && explicitNetPrice !== "") {
        const numericExplicit = Number(explicitNetPrice);
        if (Number.isFinite(numericExplicit)) {
            return numericExplicit;
        }
    }
    return applyDiscountsToValue(unitPrice, discounts);
};

export const formatDiscountLabel = (value) => {
    if (value === null || value === undefined) {
        return "";
    }
    const numeric = Number(value);
    if (!Number.isNaN(numeric)) {
        return `${numeric.toLocaleString(undefined, {minimumFractionDigits: 0, maximumFractionDigits: 4})}%`;
    }
    return `${String(value)}%`;
};

export const normalizeRowProduct = (productInput, fallback = {}) => {
    if (!productInput && !fallback) {
        return null;
    }

    const source = productInput ?? {};
    const backup = fallback ?? {};
    const pricing = typeof source.pricing === "object" && source.pricing !== null
        ? source.pricing
        : null;
    const pricingListDiscounts = pricing?.list_price_discounts
        ?? pricing?.list_discount
        ?? pricing?.discount
        ?? null;
    const pricingUnitDiscounts = pricing?.unit_price_discounts
        ?? pricing?.unit_discount
        ?? pricing?.discount
        ?? null;
    const fallbackListDiscounts = backup.list_price_discounts ?? backup.list_discount ?? [];
    const fallbackUnitDiscounts = backup.unit_price_discounts
        ?? backup.discount_list
        ?? backup.discount
        ?? [];
    const normalizedListDiscounts = normalizeDiscountList(
        source.list_price_discounts
        ?? source.list_discount
        ?? pricingListDiscounts
        ?? fallbackListDiscounts
    );
    const normalizedUnitDiscounts = normalizeDiscountList(
        source.unit_price_discounts
        ?? source.discount_list
        ?? source.discount
        ?? pricingUnitDiscounts
        ?? fallbackUnitDiscounts
    );
    const pricingListPrice = pricing?.list_price ?? pricing?.price ?? null;
    const pricingUnitPrice = pricing?.unit_price ?? pricing?.price ?? null;
    const pricingNetPrice = pricing?.net_price ?? null;
    const pricingPurchasePrice = pricing?.purchase_price ?? pricing?.cost_price ?? null;
    const pricingPriceNote = pricing?.price_note ?? pricing?.note ?? null;
    const pricingLastPriceDate = pricing?.last_price_date ?? pricing?.date ?? null;

    const normalized = {
        id: source.id ?? backup.product_id ?? null,
        list_price: source.list_price ?? pricingListPrice ?? backup.list_price ?? null,
        list_price_discounts: normalizedListDiscounts,
        unit_price: source.unit_price ?? pricingUnitPrice ?? backup.unit_price ?? null,
        unit_price_discounts: normalizedUnitDiscounts,
        net_price: source.net_price ?? pricingNetPrice ?? backup.net_price ?? null,
        discount: normalizedUnitDiscounts,
        purchase_price: source.purchase_price ?? pricingPurchasePrice ?? backup.purchase_price ?? null,
        price_note: source.price_note ?? pricingPriceNote ?? backup.price_note ?? null,
        last_price_date: source.last_price_date ?? pricingLastPriceDate ?? backup.last_price_date ?? null,
        status: source.status ?? backup.status ?? null,
        name: source.name ?? backup.name ?? "",
        product_reference: source.product_reference ?? backup.product_reference ?? "",
        manufacture_reference: source.manufacture_reference ?? backup.manufacture_reference ?? "",
        product_dimensions: source.product_dimensions ?? backup.product_dimensions ?? [],
        last_prices: source.last_prices ?? backup.last_prices ?? null,
    };

    const hasUnitDiscount = Array.isArray(normalized.unit_price_discounts)
        ? normalized.unit_price_discounts.length > 0
        : normalized.unit_price_discounts !== null && normalized.unit_price_discounts !== undefined && normalized.unit_price_discounts !== "";
    const hasListDiscount = Array.isArray(normalized.list_price_discounts)
        ? normalized.list_price_discounts.length > 0
        : normalized.list_price_discounts !== null && normalized.list_price_discounts !== undefined && normalized.list_price_discounts !== "";

    const hasValues = (normalized.id !== null && normalized.id !== undefined)
        || (normalized.list_price !== null && normalized.list_price !== undefined)
        || hasListDiscount
        || hasUnitDiscount
        || (normalized.unit_price !== null && normalized.unit_price !== undefined)
        || (normalized.net_price !== null && normalized.net_price !== undefined)
        || (normalized.purchase_price !== null && normalized.purchase_price !== undefined)
        || (normalized.price_note !== null && normalized.price_note !== undefined && normalized.price_note !== "")
        || (normalized.last_price_date !== null && normalized.last_price_date !== undefined && normalized.last_price_date !== "")
        || (normalized.name !== null && normalized.name !== undefined && normalized.name !== "")
        || (normalized.product_reference !== null && normalized.product_reference !== undefined && normalized.product_reference !== "")
        || (normalized.manufacture_reference !== null && normalized.manufacture_reference !== undefined && normalized.manufacture_reference !== "")
        || (normalized.last_prices !== null && normalized.last_prices !== undefined);

    return hasValues ? normalized : null;
};

export const deriveRowsFromDocument = (document, fallbackCurrencySymbol = "") => {
    const items = document?.bodies ?? document?.items ?? document?.products ?? [];
    const documentCurrencySymbol = resolveDocumentCurrencySymbol(document, fallbackCurrencySymbol);
    const documentCurrencyCode = document?.currency?.code
        ?? document?.currency_code
        ?? document?.company?.currency?.code
        ?? null;
    const documentDeliveryDate = document?.delivery_date ?? "";
    if (!Array.isArray(items) || items.length === 0) {
        return [];
    }

    return items.map((item, index) => {
        const product = item.product ?? {};
        const pictureReference = resolveAssetReference([
            item.picture,
            item.picture_url,
            item.pictureReference,
            item.product_picture,
            product.picture,
            product.picture_url,
            product.pictureReference,
            product.main_picture,
            product.thumbnail,
            product.product_picture,
            product.pictures,
        ]);
        const packaging = Array.isArray(product.product_dimensions)
            ? product.product_dimensions.find((dimension) => dimension.default_dimension)
            : null;
        const normalizedListPriceDiscounts = normalizeDiscountList(
            item.list_price_discounts
            ?? item.list_discount
            ?? product.list_price_discounts
            ?? product.list_discount
            ?? []
        );
        const normalizedUnitDiscounts = normalizeDiscountList(
            item.unit_price_discounts
            ?? item.discount_list
            ?? item.discount
            ?? item.discount_string
            ?? product.unit_price_discounts
            ?? product.discount
            ?? []
        );
        const lastPrices = item.last_prices ?? product.last_prices ?? null;
        const quantity = Number(item.quantity ?? 0);
        const listPrice = Number(item.list_price ?? product.list_price ?? 0);
        const unitPrice = Number(item.unit_price ?? product.unit_price ?? 0);
        const netPrice = Number(
            deriveNetPrice(
                unitPrice,
                normalizedUnitDiscounts,
                item.net_price ?? item.discount_price
            )
        );
        const totalNetPrice = Number(
            item.total_net_price
            ?? item.total_price
            ?? calculateTotalFromNetPrice(quantity, netPrice)
        );
        const itemCurrencyCode = item.currency?.code
            ?? item.currency_code
            ?? documentCurrencyCode
            ?? null;
        const itemCurrencySymbol = item.currency?.currency_symbol
            ?? item.currency_symbol
            ?? (itemCurrencyCode ? getCurrencySymbolFromCode(itemCurrencyCode) : null)
            ?? documentCurrencySymbol
            ?? fallbackCurrencySymbol
            ?? "";
        const rawPosition = item.position ?? item.row_position ?? item.line_position ?? item.line ?? null;
        const numericPosition = typeof rawPosition === "number"
            ? rawPosition
            : (rawPosition !== null && rawPosition !== undefined && rawPosition !== "")
                ? Number(rawPosition)
                : null;
        const position = Number.isFinite(numericPosition) ? numericPosition : (index + 1);
        const confirmedQuantityEvents = collectConfirmedQuantityEvents(item);
        const confirmedQuantityEventsCountCandidates = [
            toNumericOrNull(item.confirmed_quantity_events_count),
            toNumericOrNull(item.confirmedQuantityEventsCount),
            toNumericOrNull(item.confirmed_quantity_count),
            toNumericOrNull(item.confirmedQuantityCount),
        ];
        const confirmedQuantityEventsCount = confirmedQuantityEventsCountCandidates.find((value) => value !== null);
        const confirmedQuantityTotalCandidates = [
            toNumericOrNull(item.confirmed_quantity_total),
            toNumericOrNull(item.confirmedQuantityTotal),
            toNumericOrNull(item.confirmed_quantity_sum),
            toNumericOrNull(item.confirmedQuantitySum),
            toNumericOrNull(item.total_confirmed_quantity),
            toNumericOrNull(item.totalConfirmedQuantity),
            toNumericOrNull(item.total_confirmed_qty),
            toNumericOrNull(item.totalConfirmedQty),
            toNumericOrNull(item.confirmed_qty_total),
            toNumericOrNull(item.confirmedQtyTotal),
            toNumericOrNull(item.confirmed_qty),
            toNumericOrNull(item.confirmedQty),
            toNumericOrNull(item.confirmed_quantity),
            toNumericOrNull(item.confirmedQuantity),
        ];
        const confirmedQuantityTotalCandidate = confirmedQuantityTotalCandidates.find((value) => value !== null);
        const confirmedQuantityTotal = confirmedQuantityTotalCandidate !== undefined
            ? confirmedQuantityTotalCandidate
            : sumEventQuantities(confirmedQuantityEvents);

        return {
            id: String(item.id ?? item.product_id ?? item.row_id ?? index),
            position,
            name: item.name ?? "",
            note: item.note ?? item.internal_note ?? "",
            picture: pictureReference ?? null,
            product: normalizeRowProduct(product, item),
            product_reference: item.product_reference ?? "",
            manufacture_reference: product?.manufacture_reference ?? item.manufacture_reference ?? "",
            delivery_date: item.delivery_date ?? documentDeliveryDate ?? "",
            quantity,
            list_price: listPrice,
            list_price_discounts: normalizedListPriceDiscounts,
            unit_price: unitPrice,
            unit_price_discounts: normalizedUnitDiscounts,
            purchase_price: Number(item.purchase_price ?? 0),
            net_price: netPrice,
            total_net_price: totalNetPrice,
            discount_price: netPrice,
            total: totalNetPrice,
            volume: Number(item.volume ?? packaging?.volume ?? 0),
            gross_weight: Number(item.gross_weight ?? packaging?.gross_weight ?? 0),
            net_weight: Number(item.net_weight ?? packaging?.net_weight ?? 0),
            qty_per_packaging: Number(packaging?.qty_per_packaging ?? item.qty_per_packaging ?? 0),
            length: Number(item.length ?? packaging?.length ?? 0),
            width: Number(item.width ?? packaging?.width ?? 0),
            height: Number(item.height ?? packaging?.height ?? 0),
            radius: Number(item.radius ?? packaging?.radius ?? 0),
            currency_symbol: itemCurrencySymbol,
            currency_code: itemCurrencyCode,
            last_prices: lastPrices,
            confirmed_quantity_events: confirmedQuantityEvents,
            confirmed_quantity_events_count: confirmedQuantityEventsCount !== undefined
                ? Number(confirmedQuantityEventsCount)
                : confirmedQuantityEvents.length,
            confirmed_quantity_total: confirmedQuantityTotal,
            hide: resolveRowHiddenFlag(item),
        };
    });
};

export const extractBodyItems = (payload) => {
    if (!payload) {
        return [];
    }

    if (Array.isArray(payload)) {
        return payload;
    }

    if (Array.isArray(payload.bodies)) {
        return payload.bodies;
    }

    if (Array.isArray(payload.items)) {
        return payload.items;
    }

    if (Array.isArray(payload.body)) {
        return payload.body;
    }

    if (Array.isArray(payload.products)) {
        return payload.products;
    }

    if (Array.isArray(payload.documentBody)) {
        return payload.documentBody;
    }

    const camelDocumentBody = payload?.documentBody;
    if (camelDocumentBody && typeof camelDocumentBody === "object") {
        const key = ["items", "body", "bodies", "rows"].find((candidate) => Array.isArray(camelDocumentBody[candidate]));
        if (key) {
            return camelDocumentBody[key];
        }
    }

    if (Array.isArray(payload.document_body)) {
        return payload.document_body;
    }

    const snakeDocumentBody = payload?.document_body;
    if (snakeDocumentBody && typeof snakeDocumentBody === "object") {
        const key = ["items", "body", "bodies", "rows"].find((candidate) => Array.isArray(snakeDocumentBody[candidate]));
        if (key) {
            return snakeDocumentBody[key];
        }
    }

    return [];
};

export const normalizeBodyRows = (payload, baseDocument, fallbackCurrencySymbol) => deriveRowsFromDocument({
    ...baseDocument,
    bodies: extractBodyItems(payload),
}, fallbackCurrencySymbol);

export const DATEPICKER_PORTAL_ID = "app-react-datepicker-portal";

export const parseDateValue = (value) => {
    if (!value) {
        return null;
    }
    let parsed = moment(value, moment.ISO_8601, true);
    if (!parsed.isValid()) {
        parsed = moment(value, "YYYY-MM-DD", true);
    }
    if (!parsed.isValid()) {
        return null;
    }
    return parsed.toDate();
};

export const formatDateValue = (date) => {
    if (!date) {
        return "";
    }
    return moment(date).format("YYYY-MM-DD");
};

export const normalizeNumericValue = (value) => {
    if (value === null || value === undefined || value === "") {
        return "";
    }
    const numeric = Number(value);
    return Number.isNaN(numeric) ? "" : numeric;
};

export const prepareApiValue = (field, value) => {
    if (NUMERIC_FIELDS.has(field)) {
        if (value === "" || value === null || value === undefined) {
            return null;
        }
        const numeric = Number(value);
        return Number.isNaN(numeric) ? null : numeric;
    }

    if (field === "discount_list" || field === "list_price_discounts" || field === "unit_price_discounts") {
        const normalized = normalizeDiscountList(value);
        return stringifyDiscountList(normalized);
    }

    if (field === "delivery_date") {
        if (!value) {
            return null;
        }

        if (value instanceof Date) {
            return moment(value).format("YYYY-MM-DD HH:mm:ss");
        }

        if (typeof value === "string") {
            const trimmed = value.trim();
            if (!trimmed) {
                return null;
            }

            const isoMatch = moment(trimmed, moment.ISO_8601, true);
            if (isoMatch.isValid()) {
                return isoMatch.format("YYYY-MM-DD HH:mm:ss");
            }

            const dateTimeMatch = moment(trimmed, "YYYY-MM-DD HH:mm:ss", true);
            if (dateTimeMatch.isValid()) {
                return dateTimeMatch.format("YYYY-MM-DD HH:mm:ss");
            }

            const dateMatch = moment(trimmed, "YYYY-MM-DD", true);
            if (dateMatch.isValid()) {
                return `${dateMatch.format("YYYY-MM-DD")} 00:00:00`;
            }
        }

        return null;
    }

    if (field === "picture") {
        return value ?? null;
    }

    return value ?? "";
};

export const hasPriceHistoryEntries = (prices) => {
    if (!prices) {
        return false;
    }
    if (Array.isArray(prices)) {
        return prices.length > 0;
    }
    const collections = [
        prices.orders,
        prices.documents,
        prices.container_products,
    ];
    return collections.some((entries) => Array.isArray(entries) && entries.length > 0);
};

export const getPriceHistorySections = (prices, t) => {
    if (Array.isArray(prices)) {
        return [
            {
                key: "orders",
                label: t("app.quotations.history_orders", {defaultValue: "Ordini"}),
                entries: prices,
            },
        ];
    }
    const history = prices ?? {};
    return [
        {
            key: "orders",
            label: t("app.quotations.history_orders", {defaultValue: "Ordini"}),
            entries: Array.isArray(history.orders) ? history.orders : [],
        },
        {
            key: "documents",
            label: t("app.quotations.history_quotation", {defaultValue: "Offerte"}),
            entries: Array.isArray(history.documents) ? history.documents : [],
        },
        {
            key: "container_products",
            label: t("app.quotations.history_container_products", {defaultValue: "Prodotti in container"}),
            entries: Array.isArray(history.container_products) ? history.container_products : [],
        },
    ].filter((section) => section.entries.length > 0);
};
